import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Search, User, Heart, ShoppingBag, Menu, X, ChevronDown } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { cn } from '../../lib/utils';
import { resolveImageUrl } from '../../lib/imageUrl';
import { VIPHeaderBadge } from '../../pages/VIPProfilePage';

const API_URL = '';

// Special pseudo-categories use boolean flag filters, not the real `category` query param.
// (Products tagged is_nouvel_arrivage=true / is_promotion=true don't carry a matching category value.)
const categoryHref = (id) => {
  if (id === 'nouvel-arrivage') return '/shop?is_nouvel_arrivage=true';
  if (id === 'promotion') return '/shop?is_promotion=true';
  return `/shop?category=${id}`;
};

// Fallback categories (used while API loads)
const FALLBACK_CATEGORIES = [
  { id: 'nouvel-arrivage', name: 'NOUVEL ARRIVAGE', highlight: true },
  { id: 'pyjama', name: 'Pyjama' },
  { id: 'sous-vetements', name: 'Sous Vetements' },
  { id: 'ensembles', name: 'Ensembles' },
  { id: 'nuisette', name: 'Nuisette' },
  { id: 'deshabille', name: 'Deshabille' },
  { id: 'kimono', name: 'Kimono' },
  { id: 'corset-body', name: 'Corset & Body' },
  { id: 'combishort', name: 'Combishort' },
  { id: 'sport', name: 'Sport' },
  { id: 'beaute', name: 'Beaute' },
  { id: 'accessoires', name: 'Accessoires' },
  { id: 'promotion', name: 'PROMOTION', highlight: true },
];

export const Header = ({ onCartClick, onSearchClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [categories, setCategories] = useState(FALLBACK_CATEGORIES);
  const { user, login, logout } = useAuth();
  const { itemCount, setIsOpen: setCartOpen } = useCart();
  const location = useLocation();

  useEffect(() => {
    fetch(`${API_URL}/api/site-settings`)
      .then(r => r.ok ? r.json() : {})
      .then(d => { if (d.logo_url) setLogoUrl(d.logo_url); })
      .catch(() => {});
    // Fetch categories from API
    fetch(`${API_URL}/api/categories`)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.categories?.length) {
          setCategories(d.categories.map(c => ({
            id: c.id,
            name: c.id === 'nouvel-arrivage' ? 'NOUVEL ARRIVAGE' : c.id === 'promotion' ? 'PROMOTION' : c.name,
            highlight: c.id === 'nouvel-arrivage' || c.id === 'promotion',
          })));
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsSearchOpen(false);
  }, [location]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.location.href = `/shop?search=${encodeURIComponent(searchQuery)}`;
    }
  };

  return (
    <>
      {/* Main Header */}
      <header 
        className={cn(
          "sticky top-0 z-50 transition-all duration-300",
          isScrolled ? "header-glass shadow-sm" : "bg-[#FCFBF9]"
        )}
        data-testid="main-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Top Row: Menu + Logo + Actions */}
          <div className="flex items-center justify-between h-16 md:h-20 relative">
            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 -ml-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-btn"
              aria-label="Menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            {/* Spacer left on desktop */}
            <div className="hidden lg:block w-32" />

            {/* Logo — Centered */}
            <Link 
              to="/" 
              className="flex items-center"
              data-testid="logo-link"
            >
              <img 
                src={resolveImageUrl(logoUrl) || "/bloom-logo.png"} 
                alt="Bloom Lingerie" 
                className="h-12 md:h-14 w-auto object-contain"
              />
            </Link>

            {/* Actions */}
            <div className="flex items-center space-x-2 md:space-x-4">
              {/* Search Toggle */}
              <button
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                className="p-2 hover:bg-[#F4E3E1] rounded-full transition-colors"
                data-testid="search-toggle"
                aria-label="Rechercher"
              >
                <Search size={20} />
              </button>

              {/* User Account */}
              {user ? (
                <div className="relative group">
                  <button 
                    className="p-2 hover:bg-[#F4E3E1] rounded-full transition-colors flex items-center gap-1"
                    data-testid="user-menu-btn"
                  >
                    <User size={20} />
                    <ChevronDown size={14} className="hidden lg:block" />
                  </button>
                  <div className="absolute right-0 mt-2 w-48 bg-white shadow-lg py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                    <div className="px-4 py-2 border-b flex items-center gap-2">
                      <p className="text-sm text-[#6B6A68]">{user.name}</p>
                      <VIPHeaderBadge level={user.vip_tier || 'standard'} label={user.vip_label || ''} />
                    </div>
                    <Link to="/loyalty" className="block px-4 py-2 text-sm hover:bg-[#F4E3E1] text-[#D4AF37] font-medium" data-testid="vip-link">
                      Mon statut VIP
                    </Link>
                    <Link to="/account" className="block px-4 py-2 text-sm hover:bg-[#F4E3E1]" data-testid="account-link">
                      Mon Compte
                    </Link>
                    <Link to="/orders" className="block px-4 py-2 text-sm hover:bg-[#F4E3E1]" data-testid="orders-link">
                      Mes Commandes
                    </Link>
                    <Link to="/wishlist" className="block px-4 py-2 text-sm hover:bg-[#F4E3E1]" data-testid="wishlist-link">
                      Ma Wishlist
                    </Link>
                    {user.role === 'admin' && (
                      <Link to="/bloom-gestion-secure" className="block px-4 py-2 text-sm hover:bg-[#F4E3E1] text-[#D4AF37]" data-testid="admin-link">
                        Admin
                      </Link>
                    )}
                    <button 
                      onClick={logout}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-[#F4E3E1] text-[#C87979]"
                      data-testid="logout-btn"
                    >
                      Déconnexion
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={login}
                  className="p-2 hover:bg-[#F4E3E1] rounded-full transition-colors"
                  data-testid="login-btn"
                  aria-label="Connexion"
                >
                  <User size={20} />
                </button>
              )}

              {/* Wishlist */}
              <Link
                to="/wishlist"
                className="p-2 hover:bg-[#F4E3E1] rounded-full transition-colors hidden lg:block"
                data-testid="wishlist-icon"
                aria-label="Wishlist"
              >
                <Heart size={20} />
              </Link>

              {/* Cart */}
              <button
                onClick={() => setCartOpen(true)}
                className="p-2 hover:bg-[#F4E3E1] rounded-full transition-colors relative"
                data-testid="cart-btn"
                aria-label="Panier"
              >
                <ShoppingBag size={20} />
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#1A1514] text-white text-xs flex items-center justify-center rounded-full">
                    {itemCount}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Desktop Category Navigation — Second Row */}
          <nav className="hidden lg:flex items-center justify-center gap-6 xl:gap-8 h-10 border-t border-[#E0DFDD]/60" data-testid="desktop-nav">
            {categories.map((category) => (
              <Link
                key={category.id}
                to={categoryHref(category.id)}
                className={cn(
                  "text-xs font-medium tracking-wide transition-colors whitespace-nowrap",
                  category.highlight 
                    ? "text-[#C87979] hover:text-[#D4AF37]" 
                    : "text-[#1A1514] hover:text-[#D4AF37]"
                )}
                data-testid={`nav-${category.id}`}
              >
                {category.name}
              </Link>
            ))}
          </nav>
        </div>

        {/* Search Bar */}
        {isSearchOpen && (
          <div className="border-t border-[#E0DFDD] py-4 px-4" data-testid="search-bar">
            <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rechercher un produit..."
                  className="w-full py-3 px-4 pr-12 border border-[#E0DFDD] focus:border-[#1A1514] outline-none text-sm"
                  data-testid="search-input"
                  autoFocus
                />
                <button 
                  type="submit"
                  className="absolute right-4 top-1/2 -translate-y-1/2"
                  data-testid="search-submit"
                >
                  <Search size={18} className="text-[#6B6A68]" />
                </button>
              </div>
            </form>
          </div>
        )}
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" data-testid="mobile-menu">
          <div className="fixed inset-0 bg-black/50" onClick={() => setIsMobileMenuOpen(false)} />
          <div className="fixed top-0 left-0 bottom-0 w-72 bg-[#FCFBF9] p-6 overflow-y-auto">
            <div className="mb-8">
              <h2 className="font-serif text-2xl">Menu</h2>
            </div>
            <nav className="space-y-4">
              {categories.map((category) => (
                <Link
                  key={category.id}
                  to={categoryHref(category.id)}
                  className={cn(
                    "block py-2 font-medium border-b border-[#E0DFDD]",
                    category.highlight ? "text-[#C87979]" : "text-[#1A1514]"
                  )}
                  data-testid={`mobile-nav-${category.id}`}
                >
                  {category.name}
                </Link>
              ))}
              <Link 
                to="/shop" 
                className="block py-2 text-[#D4AF37] font-medium"
                data-testid="mobile-shop-all"
              >
                Voir Tout
              </Link>
            </nav>
            <div className="mt-8 pt-8 border-t border-[#E0DFDD]">
              <Link to="/size-guide" className="block py-2 text-sm text-[#6B6A68]">
                Guide des Tailles
              </Link>
              <Link to="/about" className="block py-2 text-sm text-[#6B6A68]">
                Notre Histoire
              </Link>
              <Link to="/contact" className="block py-2 text-sm text-[#6B6A68]">
                Contact
              </Link>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
